import React, { useState, useEffect } from 'react';
import { supabase, getAdminApiUrl } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface MFAManagementProps {
  onClose: () => void;
}

interface MFAFactor {
  id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const MFAManagement: React.FC<MFAManagementProps> = ({ onClose }) => {
  const { signOut } = useAuth();
  const [factors, setFactors] = useState<MFAFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    loadMFAFactors();
  }, []);

  const loadMFAFactors = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'mfaListFactors'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load MFA factors');
      }

      const data = await response.json();
      setFactors(data.factors || []);
    } catch (error: any) {
      setError(error.message || 'Failed to load MFA factors');
    } finally {
      setLoading(false);
    }
  };

  const removeMFAFactor = async (factorId: string) => {
    const isLastFactor = factors.length === 1;
    
    // Show different confirmation message for last factor
    const confirmMessage = isLastFactor 
      ? '⚠️ WARNING: This is your last MFA factor! Removing it will disable MFA and automatically log you out. You will need to set up MFA again on your next login. Are you sure you want to continue?'
      : 'Are you sure you want to remove this MFA factor? This will disable multi-factor authentication for your account.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setActionLoading(factorId);
      setError('');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No active session');
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'mfaUnenroll',
          data: {
            factorId: factorId
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove MFA factor');
      }

      if (isLastFactor) {
        // This was the last factor - show logout message and sign out
        setSuccess('Last MFA factor removed. Logging you out for security...');
        setIsLoggingOut(true);
        
        // Wait a moment to show the message, then logout
        setTimeout(async () => {
          try {
            await signOut();
          } catch (error) {
            // Force redirect to login page if signOut fails
            window.location.href = '/';
          }
        }, 2000);
      } else {
        // Not the last factor - normal flow
        setSuccess('MFA factor removed successfully');
        await loadMFAFactors();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to remove MFA factor');
    } finally {
      if (!isLastFactor) {
        setActionLoading(null);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Verified
          </span>
        );
      case 'unverified':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Unverified
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading MFA settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Multi-Factor Authentication</h3>
              <p className="text-sm text-gray-600">Manage your MFA settings</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          )}

          {/* MFA Status Overview */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-blue-900">
                    {factors.length > 0 ? 'MFA Enabled' : 'MFA Not Set Up'}
                  </h4>
                  <p className="text-sm text-blue-700">
                    {factors.length > 0 
                      ? `${factors.length} authenticator${factors.length > 1 ? 's' : ''} configured`
                      : 'Multi-factor authentication is not enabled for your account'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* MFA Factors List */}
          {factors.length > 0 ? (
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900">Configured Authenticators</h4>
              
              {/* Warning for last factor */}
              {factors.length === 1 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-red-800">
                      <p className="font-medium mb-1">⚠️ Last MFA Factor</p>
                      <p>
                        This is your only MFA factor. Removing it will disable MFA and automatically log you out. 
                        You will need to set up MFA again on your next login.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {factors.map((factor) => (
                <div key={factor.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Authenticator App</p>
                          <p className="text-xs text-gray-500">
                            Added {formatDate(factor.created_at)}
                          </p>
                        </div>
                        {getStatusBadge(factor.status)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeMFAFactor(factor.id)}
                      disabled={actionLoading === factor.id || isLoggingOut}
                      className={`ml-4 px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                        factors.length === 1 
                          ? 'text-red-700 bg-red-100 hover:bg-red-200 border border-red-300' 
                          : 'text-red-600 bg-red-50 hover:bg-red-100'
                      }`}
                    >
                      {actionLoading === factor.id ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {factors.length === 1 ? 'Logging out...' : 'Removing...'}
                        </div>
                      ) : (
                        factors.length === 1 ? 'Remove & Logout' : 'Remove'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No MFA Configured</h4>
              <p className="text-sm text-gray-600 mb-4">
                Multi-factor authentication is not enabled for your account. Set it up to add an extra layer of security.
              </p>
            </div>
          )}

          {/* Security Information */}
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Security Notice</p>
                <p>
                  MFA is required for admin accounts. If you remove all MFA factors, you may lose access to admin features.
                  Make sure to set up a backup authenticator before removing your primary one.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoggingOut}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoggingOut ? 'Logging out...' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAManagement;
